import { useMemo, useState } from 'react';
import { cn } from '#client/lib/cn';
import { Checkbox } from './Checkbox';
import { SearchFilter } from './SearchFilter';
import { Spinner } from './Spinner';

interface CheckboxPickerProps<T extends { id: number }> {
  items: T[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  label: (item: T) => string;
  /** Extra text used when filtering, e.g. a code. */
  searchText?: ((item: T) => string) | undefined;
  loading?: boolean | undefined;
  searchPlaceholder?: string | undefined;
  emptyState?: string | undefined;
  error?: string | undefined;
  /** Read-only: a locked report still shows its comments, but cannot change them. */
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Scrollable, filterable checkbox list for many-to-many selection (lab-test
 * parameters, patient lab tests, report comments).
 *
 * Selection is kept as a single id array so it survives filtering and there
 * is no second copy of the selection to disagree with.
 */
export function CheckboxPicker<T extends { id: number }>({
  items,
  selectedIds,
  onChange,
  label,
  searchText,
  loading = false,
  searchPlaceholder = 'Search',
  emptyState = 'Nothing to choose from',
  error,
  disabled = false,
  className,
}: CheckboxPickerProps<T>) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      `${label(item)} ${searchText?.(item) ?? ''}`.toLowerCase().includes(needle),
    );
  }, [items, query, label, searchText]);

  function handleToggle(id: number, checked: boolean) {
    onChange(checked ? [...selectedIds, id] : selectedIds.filter((value) => value !== id));
  }

  return (
    <div className={cn('c-picker', error && 'c-picker--invalid', className)}>
      <SearchFilter value={query} onChange={setQuery} placeholder={searchPlaceholder} />

      <div className="c-picker__list">
        {loading && (
          <div className="c-picker__state">
            <Spinner />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="c-picker__state">{query ? 'No matches' : emptyState}</p>
        )}

        {!loading &&
          filtered.map((item) => (
            <div className="c-picker__item" key={item.id}>
              <Checkbox
                label={label(item)}
                checked={selected.has(item.id)}
                disabled={disabled}
                onChange={(event) => handleToggle(item.id, event.target.checked)}
              />
            </div>
          ))}
      </div>

      <div className="c-picker__footer">
        <span>{selectedIds.length} selected</span>
        {selectedIds.length > 0 && !disabled && (
          <button type="button" className="c-picker__clear" onClick={() => onChange([])}>
            Clear all
          </button>
        )}
      </div>

      {error && <p className="c-picker__error">{error}</p>}
    </div>
  );
}
